import { ReactElement, useEffect, useRef, useState } from 'react';
import * as ApiModels from '@typings/api-models';
import { useToast } from '@hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Modal from '@components/Modal';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import XCircle from '@components/Icons/XCircle';
import useUserWorkflowContext from '@/hooks/useUserWorkflowContext';
import ProgressIndicator from '@components/ProgressIndicator';
import CreditFinancialReport from './CreditFinancialReport';
import SelectCustomer from './SelectCustomer';
import ApprovalRequestSent from './ApprovalRequestSent';

import s from './CreditReportModal.module.scss';
import CreditReportForm from './CreditReportForm';

type Props = {
  creator: Pick<ApiModels.Creator, 'slug' | 'isB2BEnabled'> | null;
  creditReportId: string;
  userWorkflowId?: string;
  onClose: () => void;
  onConfirm: () => unknown;
};

type Customer = Pick<ApiModels.Person, 'id' | 'name' | 'email' | 'contactPhoneNumber'>;

type FlowType = 'B2B' | 'B2C';

const CreditReportModal = ({
  creator,
  creditReportId,
  userWorkflowId,
  onConfirm,
  onClose,
}: Props): ReactElement => {
  const { collaborators } = useUserWorkflowContext();
  const customers = (collaborators?.filter(
    (collaborator) =>
      collaborator.source === 'customer'
      || collaborator.source === 'organization-member',
  ) ?? []).concat(
    collaborators?.filter(
      (collaborator) =>
        collaborator.source !== 'creator'
        && collaborator.source !== 'creator-team-member'
        && collaborator.source !== 'customer'
        && collaborator.source !== 'organization-member',
    ) ?? [],
  );
  const flow = creator?.isB2BEnabled ? 'B2B' : 'B2C';
  const [selectedPrice, setSelectedPrice] = useState({ currency: '', numBookings: 1, price: 0, priceId: ''});
  const getSteps = (reportFlow: FlowType, numBookings: number): string[] => {
    if (reportFlow === 'B2B') {
      return ['Credit and Financial Report', 'Select Customer', 'Confirm Main Debtor', 'Select Second Debtor', 'Confirm Second Debtor'];
    }
    return numBookings === 1
      ? ['Credit and Financial Report', 'Select Debtor', 'Approval Request Sent']
      : ['Credit and Financial Report', 'Select Debtor', '', 'Select Second Debtor', 'Approval Request Sent'];
  };
  const flowSteps = getSteps(flow ?? 'B2C', selectedPrice.numBookings);
  const [currentStep, setCurrentStep] = useState(0);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [secondCustomer, setSecondCustomer] = useState<Customer | null>(null);
  const [userInfoState, setUserInfoState] = useState<{
    data: Record<string, ApiModels.CreditReportUserInfo> | null;
    updated: boolean;
  }>({ data: null, updated: false });
  const [approvalModalResponseType, setApprovalModalResponseType] = useState<string>('');
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    fetchingState: sendingApprovalRequest,
    getData: sendApprovalRequestFn,
  } = useNoodleApi(tsClient.creditReportsB2C.sendApprovalRequest);

  const {
    fetchingState: pullingB2BCreditReportData,
    getData: pullB2BCreditReportData,
  } = useNoodleApi(tsClient.creditReportsB2B.pullCreditReportData, {
    toastErrorMessage: (error) => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0]
      || 'There was an error fetching credit report. Please try again.',
    ],
    toastOnError: true,
  });
  const isFetching = sendingApprovalRequest.isFetching || pullingB2BCreditReportData.isFetching;

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
    if (currentStep === 0) {
      setApprovalModalResponseType('');
    }
    if (approvalModalResponseType !== '') {
      setCurrentStep(5);
    }
  }, [currentStep, approvalModalResponseType]);

  const handleFinalSubmit = async (debtor1: Customer, debtor2?: Customer): Promise<void> => {
    if (!selectedPrice.price || !selectedPrice.priceId || !userWorkflowId || !creator?.slug) return;
    const clientIds = debtor2 ? [debtor1.id, debtor2.id] : [debtor1.id];
    const requestPayload = {
      clientIds,
      creditReportId,
      userInfoData: userInfoState.data,
    };
    if (flow === 'B2B') {
      const response = await pullB2BCreditReportData(requestPayload);
      const errorMessage = Array.isArray(response.error?.errors) && response.error.errors.length > 0
        ? response.error.errors.join('\n')
        : 'Failed to pull the credit report. Try again later or contact support.';
      setApprovalModalResponseType(response.data?.message === 'OK'? 'Success': errorMessage);
    } else {
      const response = await sendApprovalRequestFn(requestPayload);
      setApprovalModalResponseType(response.data?.message === 'OK' ? 'Success' : 'Error');
    }
  };

  const nextStep = async (selectedCustomer?: Customer | null, isSecondary = false): Promise<void> => {
    if (selectedCustomer) {
      if(isSecondary) {
        setSecondCustomer(selectedCustomer);
      } else {
        setCustomer(selectedCustomer);
      }
    }

    if (currentStep === 5) {
      onConfirm();
      return;
    }

    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1 && flow === 'B2B') {
      setCurrentStep(2);
      return;
    }

    if (selectedPrice.numBookings === 1) {
      if (flow === 'B2C' || currentStep === 2) {
        await handleFinalSubmit(selectedCustomer!);
      } else {
        setCurrentStep(2);
      }
      return;
    }

    if (selectedPrice.numBookings === 2) {
      if (flow === 'B2C') {
        if (currentStep === 3) {
          await handleFinalSubmit(customer!, selectedCustomer!);
        } else {
          setCurrentStep(3);
        }
      } else if (currentStep === 2) {
        setCurrentStep(3);
      } else if (currentStep === 3) {
        setCurrentStep(4);
      } else {
        await handleFinalSubmit(customer!, selectedCustomer!);
      }

    }
  };

  useEffect(() => {
    if (userInfoState.updated) {
      if (secondCustomer) {
        nextStep(secondCustomer);
      } else {
        nextStep(customer);
      }
      setUserInfoState((prev) => ({ ...prev, updated: false }));
    }
  }, [userInfoState.updated]);

  const handleUserVerification = (({ email, userInfo }: { email: string; userInfo: ApiModels.CreditReportUserInfo }): void => {
    const currentCustomer = customers.find((cust) => cust.email === email);
    if (currentCustomer) {
      setUserInfoState((prev) => ({
        data: { ...prev.data, [currentCustomer.id]: userInfo },
        updated: true,
      }));
    } else {
      setApprovalModalResponseType('Error');
    }
  });

  return (
    <Modal
      hideBottomInset
      title={isFetching ? 'Fetching the credit report' : (flowSteps[currentStep] || '')}
      onClose={isFetching
        ? undefined
        : (currentStep === 0 || currentStep === flowSteps.length)
          ? onClose
          : () => setCurrentStep((prev) => prev - 1)
      }
      ref={modalRef}
      closeIcon={currentStep === 0 || currentStep === flowSteps.length
        ? <XCircle weight="fill" size={24} color="var(--color-gray-100)" />
        : <ArrowCircleLeft weight="fill" size={24} color="var(--color-gray-100)" />}
    >
      {
        (isFetching)
          ? <ProgressIndicator isCentered hasSpace={true}/>
          : (
            <div className={s.content}>
              {currentStep === 0 && <CreditFinancialReport onSelectPrice={setSelectedPrice} nextStep={nextStep} />}
              {currentStep === 1 && <SelectCustomer nextButtonText={flow === 'B2C' && selectedPrice.numBookings === 1 ? "Submit for Client Approval" : "Next"} selectedCustomer={customer} customers={customers} onCustomerSelect={(cust) => nextStep(cust)} />}
              {flow === 'B2B' && currentStep === 2 && <CreditReportForm buttonLabel={selectedPrice.numBookings === 1 ? "Fetch Credit Report" : "Next"} userInfoData={customer} handleUserVerification={handleUserVerification} />}
              {currentStep === 3 && <SelectCustomer nextButtonText={flow === 'B2B' ? "Next" : "Submit for Client Approval"} selectedCustomer={secondCustomer} customers={customers.filter(c => c.id !== customer?.id)} onCustomerSelect={(cust) => nextStep(cust, true)} />}
              {flow === "B2B" && currentStep === 4 && <CreditReportForm buttonLabel="Fetch Credit Report" userInfoData={secondCustomer} handleUserVerification={handleUserVerification} />}
              {currentStep === 5 && <ApprovalRequestSent approvalModalResponseType={approvalModalResponseType} nextStep={nextStep} isB2B={flow === "B2B"} />}
            </div>
          )
      }
    </Modal>
  );
};

export default CreditReportModal;
